import React, { useEffect, useMemo, useState } from "react";
import { useScheduleData } from "./ScheduleDataContext";

function extractStringDeep(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "object") {
    const keys = ["value", "name", "label", "display", "text", "title"];
    for (const k of keys) {
      if (typeof v[k] === "string") return v[k];
    }
    for (const val of Object.values(v)) {
      if (typeof val === "string") return val;
    }
  }
  return "";
}

function getRoleId(role) {
  const candidates = [
    role?.id, role?.workRoleId, role?.identifier, role?.connectWiseId,
    role?.value?.id,
    typeof role?.value === "number" || typeof role?.value === "string" ? role.value : undefined,
  ];
  let rid = candidates.find((x) => x !== undefined && x !== null);
  if (typeof rid === "string" && /^\d+$/.test(rid)) rid = Number(rid);
  return rid;
}

function getRoleLabel(role) {
  return (
    extractStringDeep(role?.name) ||
    extractStringDeep(role?.description) ||
    extractStringDeep(role?.workRoleName) ||
    extractStringDeep(role?.workRole) ||
    (getRoleId(role) != null ? String(getRoleId(role)) : "(unnamed role)")
  );
}

function getUserLabel(u) {
  return (
    extractStringDeep(u?.name) ||
    extractStringDeep(u?.fullName) ||
    extractStringDeep(u?.identifier) ||
    ""
  );
}

function getUserId(u) {
  const candidates = [u?.id, u?.identifier, u?.member?.identifier, u?.memberId, u?.userId];
  let uid = candidates.find((x) => x !== undefined && x !== null);
  if (typeof uid === "string" && /^\d+$/.test(uid)) uid = Number(uid);
  return uid ?? null;
}

function toDateInputValue(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function PreFilter() {
  const {
    tokenLoading,
    tokenError,
    metadataLoaded,
    workRoles,
    workRolesLoading,
    workRolesError,
    selectedWorkRoleIds,
    setSelectedWorkRoleIds,
    usersForRoles,
    usersLoading,
    selectedUserIds,
    setSelectedUserIds,
    commitPrefilter,
  } = useScheduleData();

  // Date range defaults: today to 4 weeks out
  const today = new Date();
  const defaultStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const defaultEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 28);

  const [dateStart, setDateStart] = useState(() => toDateInputValue(defaultStart));
  const [dateEnd, setDateEnd] = useState(() => toDateInputValue(defaultEnd));
  const [submitting, setSubmitting] = useState(false);

  // Role/user search (persisted to localStorage)
  const [roleQ, setRoleQ] = useState(() => {
    try { return localStorage.getItem("rm.prefilter.roleQ") || ""; } catch { return ""; }
  });
  const [userQ, setUserQ] = useState(() => {
    try { return localStorage.getItem("rm.prefilter.userQ") || ""; } catch { return ""; }
  });
  useEffect(() => {
    try { localStorage.setItem("rm.prefilter.roleQ", roleQ); } catch {}
  }, [roleQ]);
  useEffect(() => {
    try { localStorage.setItem("rm.prefilter.userQ", userQ); } catch {}
  }, [userQ]);

  const roleItems = useMemo(() => {
    const needle = roleQ.trim().toLowerCase();
    if (!needle) return workRoles || [];
    return (workRoles || []).filter((r) => getRoleLabel(r).toLowerCase().includes(needle));
  }, [workRoles, roleQ]);

  const userItems = useMemo(() => {
    const needle = userQ.trim().toLowerCase();
    if (!needle) return usersForRoles || [];
    return (usersForRoles || []).filter((u) => getUserLabel(u).toLowerCase().includes(needle));
  }, [usersForRoles, userQ]);

  // Local staged role selection with debounced commit
  const [stagedRoleIds, setStagedRoleIds] = useState(() => selectedWorkRoleIds);
  useEffect(() => {
    const handle = setTimeout(() => {
      const sortedA = [...selectedWorkRoleIds].sort();
      const sortedB = [...stagedRoleIds].sort();
      const same = sortedA.length === sortedB.length && sortedA.every((v, i) => v === sortedB[i]);
      if (!same) setSelectedWorkRoleIds(stagedRoleIds);
    }, 600);
    return () => clearTimeout(handle);
  }, [stagedRoleIds, setSelectedWorkRoleIds, selectedWorkRoleIds]);

  const selectedRoleSet = useMemo(() => new Set(stagedRoleIds), [stagedRoleIds]);
  const selectedUserSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  function toggleRole(id) {
    if (id == null) return;
    setStagedRoleIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return Array.from(s);
    });
  }

  function toggleUser(id) {
    if (id == null) return;
    setSelectedUserIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return Array.from(s);
    });
  }

  async function handleSubmit() {
    const s = new Date(dateStart);
    const e = new Date(dateEnd);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) return;
    setSubmitting(true);
    await commitPrefilter({ start: s, end: e });
  }

  const validRange = (() => {
    const s = new Date(dateStart);
    const e = new Date(dateEnd);
    return !isNaN(s.getTime()) && !isNaN(e.getTime()) && e > s;
  })();

  if (tokenLoading || (!metadataLoaded && !tokenError)) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300, color: "#90caf9" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 32, height: 32, margin: "0 auto 12px", borderRadius: "50%", border: "3px solid #90caf9", borderTopColor: "transparent", animation: "rt-spin 0.7s linear infinite" }} />
          Loading...
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#ef5350" }}>
        Failed to acquire token: {tokenError.message}
      </div>
    );
  }

  const containerStyle = {
    maxWidth: 900,
    margin: "24px auto",
    padding: 24,
    background: "var(--blue_accent, #2a3f5f)",
    borderRadius: 12,
    color: "var(--white_coordinating, #fff)",
    border: "1px solid rgba(255,255,255,0.1)",
  };

  const sectionStyle = {
    marginBottom: 20,
  };

  const inputStyle = {
    padding: "8px 12px",
    borderRadius: 6,
    border: "1px solid #ccc",
    background: "#fff",
    color: "#000",
    fontSize: 14,
  };

  const listBoxStyle = {
    maxHeight: 200,
    overflow: "auto",
    border: "1px solid #ccc",
    borderRadius: 4,
    background: "#fff",
    color: "#000",
  };

  return (
    <div style={containerStyle}>
      <h2 style={{ margin: "0 0 4px", fontSize: 22 }}>Set Up Your View</h2>
      <p style={{ margin: "0 0 20px", opacity: 0.8, fontSize: 14 }}>
        Choose who you want to see and the date range to load.
      </p>

      {/* Date range */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 15 }}>Date Range</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>Based on resource Start Date</div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, opacity: 0.9 }}>Start</label>
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, opacity: 0.9 }}>End</label>
            <input
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              style={inputStyle}
            />
          </div>
          {!validRange && dateStart && dateEnd && (
            <span style={{ fontSize: 12, color: "#ef5350" }}>End date must be after start date</span>
          )}
        </div>
      </div>

      {/* Role & User selection */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", ...sectionStyle }}>
        {/* Work Roles */}
        <div style={{ flex: "1 1 280px", minWidth: 260 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Work Roles</div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => {
                  const allIds = roleItems.map((r) => getRoleId(r)).filter((id) => id != null);
                  setStagedRoleIds(allIds);
                  setSelectedWorkRoleIds(allIds);
                }}
                style={{ background: "#fff", color: "#000", border: "1px solid #ccc", borderRadius: 4, padding: "3px 8px", fontSize: 12 }}
              >
                Select all
              </button>
              <button
                onClick={() => { setStagedRoleIds([]); setSelectedWorkRoleIds([]); }}
                style={{ background: "#fff", color: "#000", border: "1px solid #ccc", borderRadius: 4, padding: "3px 8px", fontSize: 12 }}
              >
                Clear
              </button>
            </div>
          </div>
          <input
            placeholder="Search roles..."
            value={roleQ}
            onChange={(e) => setRoleQ(e.target.value)}
            style={{ ...inputStyle, width: "100%", marginBottom: 6 }}
          />
          <div style={listBoxStyle}>
            {workRolesLoading && <div style={{ padding: 8 }}>Loading roles...</div>}
            {!workRolesLoading && workRolesError && (
              <div style={{ padding: 8, color: "#b00020" }}>Failed to load roles: {workRolesError.message}</div>
            )}
            {!workRolesLoading && !workRolesError && roleItems.length === 0 && (
              <div style={{ padding: 8 }}>No roles found</div>
            )}
            {!workRolesLoading &&
              !workRolesError &&
              roleItems.map((r) => {
                const rid = getRoleId(r);
                const rlabel = getRoleLabel(r);
                return (
                  <label key={rid ?? rlabel} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", cursor: "pointer", color: "#000" }}>
                    <input type="checkbox" checked={rid != null && selectedRoleSet.has(rid)} onChange={() => toggleRole(rid)} />
                    <span style={{ color: "#000" }}>{rlabel}</span>
                  </label>
                );
              })}
          </div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>{stagedRoleIds.length} selected</div>
        </div>

        {/* Users */}
        <div style={{ flex: "1 1 300px", minWidth: 280 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Users</div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => {
                  const allIds = userItems.map((u) => getUserId(u)).filter((id) => id != null);
                  setSelectedUserIds(allIds);
                }}
                style={{ background: "#fff", color: "#000", border: "1px solid #ccc", borderRadius: 4, padding: "3px 8px", fontSize: 12 }}
              >
                Select all
              </button>
              <button
                onClick={() => setSelectedUserIds([])}
                style={{ background: "#fff", color: "#000", border: "1px solid #ccc", borderRadius: 4, padding: "3px 8px", fontSize: 12 }}
              >
                Clear
              </button>
            </div>
          </div>
          <input
            placeholder="Search users..."
            value={userQ}
            onChange={(e) => setUserQ(e.target.value)}
            style={{ ...inputStyle, width: "100%", marginBottom: 6 }}
          />
          <div style={listBoxStyle}>
            {usersLoading && <div style={{ padding: 8 }}>Loading users...</div>}
            {!usersLoading && stagedRoleIds.length === 0 && userItems.length === 0 && (
              <div style={{ padding: 8 }}>Select work roles to see users, or leave empty to see all.</div>
            )}
            {!usersLoading && stagedRoleIds.length > 0 && userItems.length === 0 && (
              <div style={{ padding: 8 }}>No users for selected roles</div>
            )}
            {!usersLoading &&
              userItems.map((u) => {
                const uid = getUserId(u);
                const name = getUserLabel(u);
                return (
                  <label key={uid ?? name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", cursor: "pointer", color: "#000" }}>
                    <input type="checkbox" checked={uid != null && selectedUserSet.has(uid)} onChange={() => toggleUser(uid)} />
                    <span style={{ color: "#000" }}>{name}</span>
                  </label>
                );
              })}
          </div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
            {selectedUserIds.length} selected
            {selectedUserIds.length === 0 && " (showing all users)"}
          </div>
        </div>
      </div>

      {/* Selected users summary */}
      {selectedUserIds.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>Selected Users ({selectedUserIds.length})</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {selectedUserIds.map((uid) => {
              const user = (usersForRoles || []).find((u) => getUserId(u) === uid);
              const name = user ? getUserLabel(user) : String(uid);
              return (
                <span
                  key={uid}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 10px",
                    background: "rgba(21, 101, 192, 0.35)",
                    border: "1px solid rgba(144, 202, 249, 0.4)",
                    borderRadius: 16,
                    fontSize: 13,
                    color: "#e3f2fd",
                  }}
                >
                  {name}
                  <button
                    onClick={() => toggleUser(uid)}
                    aria-label={`Remove ${name}`}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#ef9a9a",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: 15,
                      lineHeight: 1,
                      fontWeight: 700,
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Submit */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
        <button
          onClick={handleSubmit}
          disabled={!validRange || submitting}
          style={{
            padding: "10px 24px",
            borderRadius: 8,
            border: "none",
            background: validRange && !submitting ? "#1565c0" : "#455a64",
            color: "#fff",
            fontWeight: 700,
            fontSize: 15,
            cursor: validRange && !submitting ? "pointer" : "not-allowed",
          }}
        >
          {submitting ? "Loading Data..." : "Load Schedule"}
        </button>
        {submitting && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#90caf9" }}>
            <span style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #90caf9", borderTopColor: "transparent", animation: "rt-spin 0.7s linear infinite" }} />
            Fetching data for your selection...
          </span>
        )}
      </div>
    </div>
  );
}
